import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { NzModalService } from 'ng-zorro-antd/modal';

import { LockModalData } from '../models/sidenav.interface';
import { LockModalType } from '../enums/sidenav.enum';
import { CollectionInfrastructureModalService } from '../../../collection-infrastructure/collection-infrastructure-modal.service';
import { CollectionInfrastructureHelperService } from '../../../collection-infrastructure/collection-infrastructure-helper.service';
import { LOCKED_FEATURE_TYPE } from '../../../collection-infrastructure/collection-infrastructure.enums';
import { UtilService } from '../../../util/util.service';
import { EventTrackerService } from '../../services/event-tracker.service';

@Injectable({
  providedIn: 'root',
})
export class SidenavLockService {
  private readonly lockedFeaturesSubject = new BehaviorSubject<string[]>([]);
  public readonly lockedFeatures$ = this.lockedFeaturesSubject.asObservable();

  constructor(
    private _modalService: NzModalService,
    private _ciModalService: CollectionInfrastructureModalService,
    private _ciHelperService: CollectionInfrastructureHelperService,
    private _utilService: UtilService,
    private _eventTracker: EventTrackerService
  ) {
    this.initializeLockedFeatures();
  }

  private initializeLockedFeatures(): void {
    this.updateLockedFeatures();
  }

  public updateLockedFeatures(): void {
    const lockedFeatures = this.determineLockedFeatures();
    this.lockedFeaturesSubject.next(lockedFeatures);
  }

  private determineLockedFeatures(): string[] {
    const lockedFeatures: string[] = [];

    // Check for EE Free Digital Menu Leads restrictions
    if (this._utilService.isEEFreeDigitalMenuLeads()) {
      const allFeatures = [
        'orders',
        'reports',
        'tableservice',
        'table-layout',
        'table-bookings',
        'restaurantAnalytics',
        'sectionsAndCounters',
        'inventory',
      ];

      lockedFeatures.push(...allFeatures);
      return ;
    }

    // Check for plan expiration
    if (this._ciHelperService.isPlanExpired()) {
      const blockedFeatures = this._ciHelperService.getBlockedFeatures();
      lockedFeatures.push(...blockedFeatures);
    }

    return [...new Set(lockedFeatures)]; // Remove duplicates
  }

  public isFeatureLocked(featureId: string): boolean {
    if(this._utilService.isEEFreeDigitalMenuLeads()) {
      const unlockedFeatures = [
        'menu',
        'promotions',
        'ee-hq',
        'outlets',
        'ai-insights',
        'help_center',
        'EDIT_RESTAURANT_DETAILS',
        'EDIT_DEVICE_DETAILS'
      ];
      return !unlockedFeatures.includes(featureId);
    }

    if (this._ciHelperService.isPlanExpired()) {
      const blockedFeatures = this._ciHelperService.getBlockedFeatures();
      return blockedFeatures.includes(featureId);
    }

    return false;
  }

  public handleLockedFeature(featureId: string, event?: Event): void {
    // Prevent navigation
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Track the lock trigger event
    this.trackLockEvent(featureId);

    // Determine the modal type and show appropriate modal
    const modalType = this.getModalType();
    this.showLockModal(modalType, featureId);
  }

  private getModalType(): LOCKED_FEATURE_TYPE {
    if (this._utilService.isEEFreeDigitalMenuLeads()) {
      return LOCKED_FEATURE_TYPE.FREE_MENU_LEADS;
    }

    if (this._ciHelperService.isPlanExpired()) {
      return LOCKED_FEATURE_TYPE.EXPIRED;
    }

    return LOCKED_FEATURE_TYPE.EXPIRED; // Default fallback
  }

  private showLockModal(type: LOCKED_FEATURE_TYPE, featureId: string): void {
    this._ciModalService.openLockedPlanModal({ type });
  }

  private trackLockEvent(featureId: string): void {
    this._eventTracker.easyeat_track_event('FEATURE_LOCKED_CLICKED', {
      feature_id: featureId,
      user_type: localStorage.getItem('user_type'),
      restaurant_id: localStorage.getItem('restaurant_id'),
    });
  }

  public createLockModalData(
    type: LockModalType,
    feature: string,
    customTitle?: string,
    customMessage?: string
  ): LockModalData {
    const defaultData = this.getDefaultLockModalData(type, feature);

    return {
      ...defaultData,
      title: customTitle || defaultData.title,
      message: customMessage || defaultData.message,
    };
  }

  private getDefaultLockModalData(
    type: LockModalType,
    feature: string
  ): LockModalData {
    switch (type) {
      case LockModalType.EXPIRED:
        return {
          type: LockModalType.EXPIRED,
          feature,
          title: 'Plan Expired',
          message:
            'Your subscription has expired. Please renew to access this feature.',
        };

      case LockModalType.FREE_MENU_LEADS:
        return {
          type: LockModalType.FREE_MENU_LEADS,
          feature,
          title: 'Upgrade Required',
          message:
            'This feature is not available in the free plan. Please upgrade to access it.',
        };

      case LockModalType.FEATURE_UNAVAILABLE:
        return {
          type: LockModalType.FEATURE_UNAVAILABLE,
          feature,
          title: 'Feature Unavailable',
          message:
            'This feature is currently not available for your account type.',
        };

      default:
        return {
          type: LockModalType.FEATURE_UNAVAILABLE,
          feature,
          title: 'Access Restricted',
          message: 'You do not have permission to access this feature.',
        };
    }
  }

  public checkFeatureAccess(featureId: string): {
    hasAccess: boolean;
    lockType?: LockModalType;
    reason?: string;
  } {
    if (!this.isFeatureLocked(featureId)) {
      return { hasAccess: true };
    }

    let lockType: LockModalType;
    let reason: string;

    if (this._utilService.isEEFreeDigitalMenuLeads()) {
      lockType = LockModalType.FREE_MENU_LEADS;
      reason = 'Feature not available in free plan';
    } else if (this._ciHelperService.isPlanExpired()) {
      lockType = LockModalType.EXPIRED;
      reason = 'Subscription expired';
    } else {
      lockType = LockModalType.FEATURE_UNAVAILABLE;
      reason = 'Feature access restricted';
    }

    return {
      hasAccess: false,
      lockType,
      reason,
    };
  }
}
